import { useCallback, useEffect } from "react";
import { X } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";

import { Button } from "./button";

interface PWAPromptProps {
	/**
	 * Interval to check for updates in milliseconds
	 * @default 3600000 (1 hour)
	 */
	updateCheckInterval?: number;
	/**
	 * Auto-hide duration in milliseconds
	 * @default 5000 (5 seconds)
	 */
	autoHideDuration?: number;
	/**
	 * Check for updates on first load
	 * @default true
	 */
	checkOnFirstLoad?: boolean;
}

/**
 * PWA Update Prompt Component
 *
 * Displays a toast notification when:
 * - App is ready to work offline
 * - A new version is available and needs refresh
 *
 * Automatically checks for updates:
 * - On first load (if checkOnFirstLoad is true)
 * - Based on the configured periodic interval
 *
 * Auto-hides after the specified duration (default 5 seconds).
 */
export function PWAPrompt({
	updateCheckInterval = 5 * 60 * 1000,
	autoHideDuration = 5000,
	checkOnFirstLoad = true,
}: PWAPromptProps) {
	const {
		offlineReady: [offlineReady, setOfflineReady],
		needRefresh: [needRefresh, setNeedRefresh],
		updateServiceWorker,
	} = useRegisterSW({
		onRegisteredSW(
			swUrl: string,
			registration: ServiceWorkerRegistration | undefined,
		) {
			if (!registration) return;

			const isActivated = registration.active?.state === "activated";

			// Check for updates on first load if enabled
			if (checkOnFirstLoad && isActivated) {
				checkForUpdates(swUrl, registration);
			}

			// Register periodic sync to check for updates
			if (updateCheckInterval > 0) {
				if (isActivated) {
					registerPeriodicSync(updateCheckInterval, swUrl, registration);
				} else if (registration.installing) {
					registration.installing.addEventListener(
						"statechange",
						(e: Event) => {
							const sw = e.target as ServiceWorker;
							if (sw.state === "activated") {
								registerPeriodicSync(updateCheckInterval, swUrl, registration);
							}
						},
					);
				}
			}
		},
		onRegisterError(error: Error) {
			console.error("SW registration error", error);
		},
	});

	const close = useCallback(() => {
		setOfflineReady(false);
		setNeedRefresh(false);
	}, [setOfflineReady, setNeedRefresh]);

	function handleUpdate() {
		updateServiceWorker(true);
	}

	// Auto-hide after specified duration
	useEffect(() => {
		if (offlineReady) {
			const timer = setTimeout(() => {
				close();
			}, autoHideDuration);

			return () => clearTimeout(timer);
		}
	}, [offlineReady, autoHideDuration, close]);

	if (!offlineReady && !needRefresh) {
		return null;
	}

	return (
		<div
			className="fixed right-4 bottom-4 z-50 max-w-md"
			role="alert"
			aria-live="assertive"
			aria-atomic="true"
		>
			<div className="flex items-start gap-3 rounded-lg border border-border bg-background p-4 shadow-lg">
				<div className="flex-1">
					<div className="mb-1 text-sm font-semibold">
						{offlineReady ? "App Ready" : "Update Available"}
					</div>
					<p className="text-sm text-muted-foreground">
						{offlineReady
							? "Latest content is now available."
							: "New content available. Click reload to update."}
					</p>
				</div>
				<div className="flex items-center gap-2">
					{needRefresh && (
						<Button size="sm" onClick={handleUpdate} className="shrink-0">
							Reload
						</Button>
					)}
					<Button
						size="sm"
						variant="ghost"
						onClick={close}
						className="shrink-0"
						aria-label="Close notification"
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}

/**
 * Performs the actual service worker update check
 * Only checks when the browser is online
 */
async function performSwUpdateCheck(
	swUrl: string,
	registration: ServiceWorkerRegistration,
) {
	// Skip if offline
	if ("onLine" in navigator && !navigator.onLine) {
		return;
	}

	try {
		const resp = await fetch(swUrl, {
			cache: "no-store",
			headers: {
				cache: "no-store",
				"cache-control": "no-cache",
			},
		});

		if (resp.status === 200) {
			await registration.update();
		}
	} catch (error) {
		console.error("Failed to check for SW updates:", error);
	}
}

/**
 * Checks for service worker updates immediately
 */
function checkForUpdates(
	swUrl: string,
	registration: ServiceWorkerRegistration,
) {
	performSwUpdateCheck(swUrl, registration);
}

/**
 * Registers a periodic check for service worker updates
 */
function registerPeriodicSync(
	period: number,
	swUrl: string,
	registration: ServiceWorkerRegistration,
) {
	if (period <= 0) return;

	setInterval(() => {
		performSwUpdateCheck(swUrl, registration);
	}, period);
}
